import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGroups } from 'src/features/groups/hooks';
import { ChatSearchFilters } from '../types';
import { groupSourceKey } from '../components/SourcesPanel';
import { useCategoryDefaults } from './useCategoryDefaults';

/** Empty-selection sentinel in the chat URL (`docHubSource` / `category`). */
export const DOC_HUB_NONE = '_none';
/**
 * Marker that the four default hub folders are selected on purpose with every
 * group source off. Legacy URLs wrote only those four keys (groups were not in
 * the source list yet); without this token those URLs mean "all defaults"
 * including groups.
 */
export const DOC_HUB_NO_GROUPS = 'nogroups';
export const DEFAULT_HUB_FOLDER_SOURCES = ['organisation', 'library'] as const;
const LEGACY_DEFAULT_HUB_FOLDER_SOURCES = [
  ['organisation', 'library', 'drafts'],
  ['organisation', 'library', 'drafts', 'workflows']
] as const;

export function sameStringSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  return b.every((item) => left.has(item));
}

function withoutUrlSentinels(values: string[]): string[] {
  return values.filter((s) => s && s !== DOC_HUB_NONE && s !== DOC_HUB_NO_GROUPS);
}

function foldLegacyHubSources(sources: string[]): string[] {
  let next = sources.filter((s) => s !== 'drafts');
  if (next.includes('workflows')) {
    next = next.filter((s) => s !== 'workflows');
    if (!next.includes('library')) next.push('library');
  }
  return next;
}

interface UseChatUrlStateReturn {
  sessionId: string | undefined;
  chatFilters: ChatSearchFilters;
  setChatFilters: (filters: ChatSearchFilters) => void;
  /** True once default category filters have been loaded from the API (or the URL already carries filters). */
  filtersReady: boolean;
  specialistId: string | null;
  setSpecialistId: (specialistId: string | null) => void;
  googleSearchPermission: boolean;
  setGoogleSearchPermission: (enabled: boolean) => void;
}

const useChatUrlState = (): UseChatUrlStateReturn => {
  const { sessionId: sessionIdParam } = useParams();
  const sessionId = sessionIdParam || undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: categoryDefaults = null } = useCategoryDefaults();
  // Extract only the default-enabled category names (e.g. ["Guidance"]).
  const defaultCategories = categoryDefaults
    ? Object.entries(categoryDefaults)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name)
    : null;

  const { data: groupsList, isFetched: groupsFetched } = useGroups();
  const defaultGroupSources = (groupsList?.groups ?? []).map((g) => groupSourceKey(g.id));

  // In-memory default: every standard folder plus every group the user belongs to.
  // This is not written to the URL — omitting `docHubSource` means unrestricted.
  const defaultDocHubSources = [...DEFAULT_HUB_FOLDER_SOURCES, ...defaultGroupSources];

  const docHubSourceParam = searchParams.get('docHubSource');
  const docHubTokens =
    docHubSourceParam == null || docHubSourceParam === DOC_HUB_NONE || docHubSourceParam === ''
      ? null
      : docHubSourceParam.split('|').filter(Boolean);
  const hasExplicitNoGroups = !!docHubTokens?.includes(DOC_HUB_NO_GROUPS);
  const parsedWithoutSentinels = docHubTokens ? withoutUrlSentinels(docHubTokens) : undefined;
  // Old clients persisted the default hub folders as "everything selected". That is
  // indistinguishable from today's folders-on/groups-off list unless we require
  // the `nogroups` marker for the latter. Detect on raw tokens so folding
  // drafts/workflows into library cannot turn a partial selection into "all".
  const isLegacyFourFolderDefault =
    !!parsedWithoutSentinels &&
    !hasExplicitNoGroups &&
    (sameStringSet(parsedWithoutSentinels, DEFAULT_HUB_FOLDER_SOURCES) ||
      LEGACY_DEFAULT_HUB_FOLDER_SOURCES.some((legacy) => sameStringSet(parsedWithoutSentinels, legacy)));
  const parsedDocHubSources =
    docHubSourceParam == null
      ? undefined
      : docHubSourceParam === DOC_HUB_NONE || docHubSourceParam === ''
        ? []
        : isLegacyFourFolderDefault
          ? undefined
          : foldLegacyHubSources(parsedWithoutSentinels ?? []);

  const categoryParam = searchParams.get('category');
  const parsedCategories =
    categoryParam == null
      ? undefined
      : categoryParam === DOC_HUB_NONE || categoryParam === ''
        ? []
        : categoryParam.split('|').filter((s) => s && s !== DOC_HUB_NONE);

  const chatFilters: ChatSearchFilters = {
    category: parsedCategories ?? defaultCategories ?? [],
    domain: searchParams.get('domain')?.split('|').filter(Boolean) || [],
    subDomain: searchParams.get('subDomain')?.split('|').filter(Boolean) || [],
    document: searchParams.get('document')?.split('|').filter(Boolean) || [],
    docHubSource: parsedDocHubSources ?? defaultDocHubSources
  };

  // Ready once API defaults are loaded (or the URL already names each filter).
  // Until then, submitting would send an empty category list (wiki off).
  // Legacy four-folder URLs are treated as omitted until groups have loaded.
  const filtersReady =
    (categoryParam != null || defaultCategories !== null) &&
    ((docHubSourceParam != null && !isLegacyFourFolderDefault) || groupsFetched);

  // Get specialist_id from URL
  const specialistId = searchParams.get('specialist_id');
  const googleSearchPermission = searchParams.get('google_search') === 'true';

  // Update URL with new filters
  const setChatFilters = (filters: ChatSearchFilters) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'docHubSource') {
        const sources = foldLegacyHubSources(withoutUrlSentinels(values));
        if (sources.length === 0) {
          newSearchParams.set(key, DOC_HUB_NONE);
        } else if (groupsFetched && sameStringSet(sources, defaultDocHubSources)) {
          newSearchParams.delete(key);
        } else if (
          groupsFetched &&
          defaultGroupSources.length > 0 &&
          sameStringSet(sources, DEFAULT_HUB_FOLDER_SOURCES)
        ) {
          newSearchParams.set(key, [...DEFAULT_HUB_FOLDER_SOURCES, DOC_HUB_NO_GROUPS].join('|'));
        } else {
          newSearchParams.set(key, sources.join('|'));
        }
        return;
      }
      if (key === 'category') {
        if (values.length === 0) {
          newSearchParams.set(key, DOC_HUB_NONE);
        } else if (defaultCategories && sameStringSet(values, defaultCategories)) {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, values.join('|'));
        }
        return;
      }
      if (values && values.length > 0) {
        newSearchParams.set(key, values.join('|'));
      } else {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Update URL with new specialist_id
  const setSpecialistId = (newSpecialistId: string | null) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (newSpecialistId) {
      newSearchParams.set('specialist_id', newSpecialistId);
    } else {
      newSearchParams.delete('specialist_id');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const setGoogleSearchPermission = (enabled: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (enabled) {
      newSearchParams.set('google_search', 'true');
    } else {
      newSearchParams.delete('google_search');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  useEffect(() => {
    // Strip default-all lists left in the URL by older clients (one write so
    // category + docHubSource stripping cannot race each other).
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (
      parsedDocHubSources &&
      parsedDocHubSources.length > 0 &&
      groupsFetched &&
      sameStringSet(parsedDocHubSources, defaultDocHubSources)
    ) {
      next.delete('docHubSource');
      changed = true;
    } else if (isLegacyFourFolderDefault && groupsFetched) {
      next.delete('docHubSource');
      changed = true;
    }
    if (
      parsedCategories &&
      parsedCategories.length > 0 &&
      defaultCategories &&
      sameStringSet(parsedCategories, defaultCategories)
    ) {
      next.delete('category');
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docHubSourceParam, categoryParam, groupsFetched, defaultDocHubSources.join('|'), defaultCategories?.join('|')]);

  return {
    sessionId,
    chatFilters,
    setChatFilters,
    filtersReady,
    specialistId,
    setSpecialistId,
    googleSearchPermission,
    setGoogleSearchPermission
  };
};

export default useChatUrlState;
